/**
 * Authentication Async Thunks
 * 
 * SOLID Principles Applied:
 * - Single Responsibility: Only handles async side effects (API calls)
 * - Separation of Concerns: Isolated from state management and data access
 * - Open/Closed: Easy to add new thunks without modifying existing ones
 * 
 * This file contains all async operations for the auth feature:
 * - Login user
 * - Register user
 * - Logout user
 * - Refresh access token
 * - Get current user
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import { authService } from '../../../services/api/auth.service';
import { twoFactorService } from '../../../services/api/twoFactor.service';
import { userService } from '../../../services/api/user.service';
import { oauthPopupService } from '../../../services/oauthPopupService';
import { tokenStore } from '../../../services/tokenStore';
import {
  IAuthResponse,
  ILoginDto,
  IRegisterDto,
  IUpdateUserProfileDto,
  IChangePasswordDto,
} from '../../../types/auth.types';
import { formatErrorMessage } from '../../../utils/errorHandling';
import { isGuestSessionProbeError } from '../../../utils/authHttpErrors';
import {
  OAUTH_CANCELLED,
  getOAuthUserMessage,
  isOAuthPopupBlocked,
} from '../../../utils/oauthErrors';
import {
  trackAuthGoogleClick,
  trackAuthGoogleFail,
  trackAuthGoogleSuccess,
  trackAuthLinkAction,
  trackAuthOneTapAccept,
  trackAuthOneTapFail,
  type AuthAnalyticsSurface,
} from '../../../utils/authAnalytics';
import { showErrorToast, showInfoToast, showSuccessToast } from '../../../utils/toast';

export type GoogleAuthIntent = 'login' | 'register';

export type GoogleAuthRequest =
  | GoogleAuthIntent
  | {
      intent?: GoogleAuthIntent;
      surface?: AuthAnalyticsSurface;
      /** Skip default success toast (e.g. auth prompt handles messaging). */
      suppressSuccessToast?: boolean;
    };

function resolveGoogleAuthRequest(
  arg: GoogleAuthRequest = 'login',
): { intent: GoogleAuthIntent; surface: AuthAnalyticsSurface } {
  if (typeof arg === 'string') {
    return {
      intent: arg,
      surface: arg === 'register' ? 'register' : 'login',
      suppressSuccessToast: false,
    };
  }
  const intent = arg.intent ?? 'login';
  const surface =
    arg.surface ?? (intent === 'register' ? 'register' : 'login');
  return {
    intent,
    surface,
    suppressSuccessToast: arg.suppressSuccessToast ?? false,
  };
}

// ============================================
// ASYNC THUNKS (Side Effects)
// ============================================

/**
 * Login User
 * 
 * @param credentials - Email and password
 * @returns Auth response with user and tokens
 * 
 * Usage:
 *   dispatch(loginUser({ email: 'user@example.com', password: 'password' }))
 */
export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials: ILoginDto, { rejectWithValue }) => {
    try {
      console.log('🔐 Login attempt:', { email: credentials.email });
      const response = await authService.login(credentials);
      console.log('✅ Login successful:', { user: response.user.email });

      if (response.requiresTwoFactor && response.twoFactorToken) {
        return response;
      }
      
      if (!credentials.silent) {
        showSuccessToast(
          'Welcome back!',
          `Logged in as ${response.user.email}`
        );
      }
      
      return response;
    } catch (error) {
      console.error('❌ Login failed:', error);
      
      // Show error toast
      showErrorToast(error, 'Login Failed');
      
      const errorMessage = formatErrorMessage(error);
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Sign in or sign up with Google (popup OAuth).
 * Backend creates the account when the Google email is new.
 */
export const authenticateWithGoogle = createAsyncThunk(
  'auth/googleOAuth',
  async (arg: GoogleAuthRequest = 'login', { rejectWithValue }) => {
    const { intent, surface, suppressSuccessToast } = resolveGoogleAuthRequest(arg);
    try {
      trackAuthGoogleClick(surface, intent);
      const result = await oauthPopupService.loginWithGoogle();

      if (!result.success) {
        if (result.error === OAUTH_CANCELLED) {
          return rejectWithValue(OAUTH_CANCELLED);
        }

        const reason = isOAuthPopupBlocked(result.error)
          ? 'popup_blocked'
          : 'oauth_failed';
        trackAuthGoogleFail(surface, reason, intent);

        const message = getOAuthUserMessage(result.error);
        showErrorToast(message || result.error || 'Google sign-in failed', 'Sign In Failed');
        return rejectWithValue(result.error || message || 'Google sign-in failed');
      }

      if (result.requiresTwoFactor && result.twoFactorToken) {
        showInfoToast(
          'Almost there',
          'Enter your authenticator code to finish signing in.',
        );
        return {
          requiresTwoFactor: true,
          twoFactorToken: result.twoFactorToken,
          user: { email: result.user?.email ?? '' },
        } as IAuthResponse;
      }

      if (!result.accessToken) {
        showErrorToast('No access token received', 'Sign In Failed');
        return rejectWithValue('No access token received');
      }

      tokenStore.setAccessToken(result.accessToken);
      const user = await authService.getCurrentUser();

      if (!suppressSuccessToast) {
        if (result.isNewUser) {
          const name = user.firstName || user.email;
          showSuccessToast('Account created. Welcome to Qumashi!', name);
        } else {
          showSuccessToast('Welcome back!', `Signed in as ${user.email}`);
        }
      }

      trackAuthGoogleSuccess(surface, intent);
      if (result.googleAccountLinked) {
        trackAuthLinkAction('link_success', {
          surface,
          method: 'google',
          intent: 'link',
        });
      }

      return {
        access_token: result.accessToken,
        user,
        isNewUser: result.isNewUser === true,
      };
    } catch (error) {
      trackAuthGoogleFail(surface, 'exception', intent);
      showErrorToast(error, 'Google Sign In Failed');
      return rejectWithValue(formatErrorMessage(error));
    }
  },
);

export { OAUTH_CANCELLED };

/**
 * Connect Google to the currently signed-in account (settings).
 */
export const linkGoogleAccount = createAsyncThunk(
  'auth/linkGoogleAccount',
  async (_, { rejectWithValue }) => {
    trackAuthLinkAction('link_click', {
      surface: 'account_settings',
      method: 'google',
      intent: 'link',
    });
    try {
      await authService.prepareGoogleLink();
      const result = await oauthPopupService.linkGoogleAccount();

      if (!result.success) {
        if (result.error === OAUTH_CANCELLED) {
          return rejectWithValue(OAUTH_CANCELLED);
        }
        const message = getOAuthUserMessage(result.error);
        showErrorToast(message || result.error || 'Could not connect Google', 'Connect Failed');
        return rejectWithValue(result.error || message || 'Could not connect Google');
      }

      trackAuthLinkAction('link_success', {
        surface: 'account_settings',
        method: 'google',
        intent: 'link',
      });

      const user = await authService.getCurrentUser();
      showSuccessToast('Google connected', 'You can now sign in with Google.');
      return user;
    } catch (error) {
      showErrorToast(error, 'Connect Failed');
      return rejectWithValue(formatErrorMessage(error));
    }
  },
);

/**
 * Sign in with Google One Tap (browser account picker / top-right prompt).
 */
export const authenticateWithGoogleOneTap = createAsyncThunk(
  'auth/googleOneTap',
  async (credential: string, { rejectWithValue }) => {
    trackAuthOneTapAccept('one_tap');
    try {
      const response = await authService.loginWithGoogleOneTap(credential);

      if (response.requiresTwoFactor && response.twoFactorToken) {
        showInfoToast(
          'Almost there',
          'Enter your authenticator code to finish signing in.',
        );
        return {
          requiresTwoFactor: true,
          twoFactorToken: response.twoFactorToken,
          user: { email: response.user?.email ?? '' },
        } as IAuthResponse;
      }

      const user = await authService.getCurrentUser();
      showSuccessToast('Welcome back!', `Signed in as ${user.email}`);

      return {
        access_token: response.access_token,
        user,
      };
    } catch (error) {
      trackAuthOneTapFail('exception');
      showErrorToast(error, 'Google Sign In Failed');
      return rejectWithValue(formatErrorMessage(error));
    }
  },
);

/**
 * Complete login with two-factor authentication code
 */
export const verifyTwoFactorLogin = createAsyncThunk(
  'auth/verifyTwoFactor',
  async (
    payload: { twoFactorToken: string; code: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await twoFactorService.verifyLogin(
        payload.twoFactorToken,
        payload.code,
      );
      showSuccessToast('Welcome back!', `Logged in as ${response.user.email}`);
      return response;
    } catch (error) {
      showErrorToast(error, 'Verification Failed');
      return rejectWithValue(formatErrorMessage(error));
    }
  },
);

/**
 * Register New User
 * 
 * @param userData - User registration data
 * @returns Auth response with user and tokens
 * 
 * Usage:
 *   dispatch(registerUser({ 
 *     email: 'user@example.com', 
 *     password: 'password',
 *     fullName: 'John Doe'
 *   }))
 */
export const registerUser = createAsyncThunk(
  'auth/register',
  async (userData: IRegisterDto, { rejectWithValue }) => {
    try {
      console.log('📝 Registration attempt:', { email: userData.email });
      const response = await authService.register(userData);
      console.log('✅ Registration successful:', { user: response.user.email });
      
      // Show success toast
      showSuccessToast(
        'Account Created!',
        `Welcome to POD Marketplace, ${userData.firstName}!`
      );
      
      return response;
    } catch (error) {
      console.error('❌ Registration failed:', error);
      
      // Show error toast
      showErrorToast(error, 'Registration Failed');
      
      const errorMessage = formatErrorMessage(error);
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Logout User
 * 
 * Clears user session and tokens
 * 
 * Usage:
 *   dispatch(logoutUser())
 */
export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await authService.logout();
      console.log('👋 Logout successful');
      
      // Show success toast
      showSuccessToast('Logged out successfully', 'See you soon!');
      
      return;
    } catch (error) {
      console.error('❌ Logout failed:', error);
      
      // Show error toast
      showErrorToast(error, 'Logout Failed');
      
      const errorMessage = formatErrorMessage(error);
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Refresh Access Token
 * 
 * Called automatically when access token expires
 * Uses refresh token from state or localStorage
 * 
 * Usage:
 *   dispatch(refreshToken())
 */
export const refreshToken = createAsyncThunk(
  'auth/refresh',
  async (_, { rejectWithValue }) => {
    try {
      // Refresh token is in httpOnly cookie - browser sends it automatically
      const response = await authService.refreshToken();
      console.log('🔄 Token refreshed successfully');
      return response;
    } catch (error) {
      if (!isGuestSessionProbeError(error)) {
        console.error('❌ Token refresh failed:', error);
      }
      const errorMessage = formatErrorMessage(error);
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Get Current User (Session Restoration)
 * 
 * Fetches current user data from the API
 * Called on app initialization to restore session
 * 
 * Usage:
 *   dispatch(getCurrentUser())
 */
export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const user = await authService.getCurrentUser();
      console.log('👤 User info fetched:', { email: user.email });
      return user;
    } catch (error) {
      if (!isGuestSessionProbeError(error)) {
        console.error('❌ Failed to fetch user:', error);
      }
      const errorMessage = formatErrorMessage(error);
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Update User Profile
 * 
 * Updates user profile information (firstName, lastName, username)
 * 
 * @param data - Profile update data
 * @returns Updated user object
 * 
 * Usage:
 *   dispatch(updateUserProfile({ firstName: 'John', lastName: 'Doe' }))
 */
export const updateUserProfile = createAsyncThunk(
  'auth/updateUserProfile',
  async (data: IUpdateUserProfileDto, { rejectWithValue }) => {
    try {
      console.log('📝 Updating user profile:', data);
      const updatedUser = await userService.updateUserProfile(data);
      console.log('✅ Profile updated successfully:', { email: updatedUser.email });
      
      // Show success toast
      showSuccessToast(
        'Profile Updated',
        'Your profile information has been updated successfully.'
      );
      
      return updatedUser;
    } catch (error) {
      console.error('❌ Failed to update profile:', error);
      
      // Show error toast
      showErrorToast(error, 'Update Failed');
      
      const errorMessage = formatErrorMessage(error);
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Change User Password
 * 
 * Changes user password when logged in
 * 
 * @param data - Password change data (currentPassword, newPassword)
 * 
 * Usage:
 *   dispatch(changePassword({ currentPassword: 'old', newPassword: 'new' }))
 */
export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async (
    data: IChangePasswordDto & { isInitialPassword?: boolean },
    { rejectWithValue },
  ) => {
    try {
      console.log('🔐 Changing password');
      await userService.changePassword(
        data.currentPassword ?? '',
        data.newPassword,
      );
      console.log('✅ Password changed successfully');

      if (data.isInitialPassword) {
        trackAuthLinkAction('set_password', {
          surface: 'account_settings',
          method: 'email',
          intent: 'login',
        });
      }

      showSuccessToast(
        data.isInitialPassword ? 'Password Enabled' : 'Password Changed',
        data.isInitialPassword
          ? 'You can now sign in with email and password.'
          : 'Your password has been updated successfully.',
      );

      return;
    } catch (error) {
      console.error('❌ Failed to change password:', error);
      
      // Show error toast
      showErrorToast(error, 'Password Change Failed');
      
      const errorMessage = formatErrorMessage(error);
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Disconnect Google from the current account.
 */
export const unlinkGoogleAccount = createAsyncThunk(
  'auth/unlinkGoogleAccount',
  async (_, { rejectWithValue }) => {
    try {
      const result = await userService.unlinkGoogleAccount();
      trackAuthLinkAction('unlink', {
        surface: 'account_settings',
        method: 'google',
        intent: 'link',
      });
      showSuccessToast('Google disconnected', result.message);
      return result;
    } catch (error) {
      showErrorToast(error, 'Disconnect Failed');
      return rejectWithValue(formatErrorMessage(error));
    }
  }
);

